import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { getTeamMembers } from '../services/api';

const TeamPage = ({ type }) => {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);

    // Category mismatch fix: Database uses 'baskan', 'yonetim', 'denetim' directly.
    const category = type;

    // UI Config
    let title = "";
    let description = "TURET'i geleceğe taşıyan dinamik ve tutkulu ekibimiz.";
    let color = "cyan";

    if (type === 'baskan') {
        title = "Topluluk Başkanı";
        color = "cyan";
    } else if (type === 'yonetim') {
        title = "Yönetim Kurulu";
        color = "cyan";
    } else if (type === 'denetim') {
        title = "Denetim Kurulu";
        color = "yellow";
    }

    useEffect(() => {
        window.scrollTo(0, 0);

        const fetchTeam = async () => {
            setLoading(true);
            try {
                let data = [];

                if (category === 'yonetim') {
                    // For 'yonetim' (Board), fetch BOTH 'yonetim' AND 'baskan'
                    const [yonetimMembers, baskanMembers] = await Promise.all([
                        getTeamMembers('yonetim'),
                        getTeamMembers('baskan')
                    ]);
                    data = [...baskanMembers, ...yonetimMembers];
                } else {
                    // Fetch by specific category
                    data = await getTeamMembers(category);
                }

                // Sort by role priority
                const sortedData = data.sort((a, b) => {
                    const getPriority = (role, cat) => {
                        const lowerRole = role.toLowerCase();
                        if (cat === 'baskan' || lowerRole.includes('topluluk başkanı')) return 0; // Highest priority
                        if (lowerRole.includes('başkanı') && !lowerRole.includes('yardımcısı')) return 1;
                        if (lowerRole.includes('başkan yardımcısı')) return 2;
                        return 3;
                    };
                    return getPriority(a.role, a.category) - getPriority(b.role, b.category);
                });

                setMembers(sortedData);

            } catch (error) {
                console.error("Ekip verisi çekilemedi:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchTeam();
    }, [type, category]);

    if (loading) {
        return (
            <div className="min-h-screen bg-modern-blue-50 flex items-center justify-center">
                <div className="text-xl font-bold text-cyan-600">Yükleniyor...</div>
            </div>
        );
    }

    return (
        <div className="bg-modern-blue-50 text-modern-blue-900 min-h-screen">
            <main className="pt-32 pb-20 px-6">
                <div className="container mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center mb-16"
                    >
                        <h1 className="text-3xl md:text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-modern-blue-900 via-modern-blue-600 to-emerald-500 mb-6 drop-shadow-sm font-heading">
                            {title}
                        </h1>
                        <p className="text-modern-blue-600 max-w-2xl mx-auto text-base leading-relaxed">
                            {description}
                        </p>
                    </motion.div>

                    {members.length === 0 ? (
                        <div className="text-center text-gray-500">
                            Bu kategoriye ait ekip üyesi bulunamadı.
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 justify-center">
                            {members.map((member, idx) => (
                                <TeamCard key={member.id || idx} {...member} index={idx} color={color} />
                            ))}
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

const TeamCard = ({ name, role, image, index, color }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.3, delay: index * 0.1 }}
        className="group relative max-w-[280px] mx-auto w-full"
    >
        {/* Removed blurred overlay */}

        <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-modern-blue-100 group-hover:border-cyan-400 transition-all relative z-10 h-full w-full aspect-[3/4]">

            {image ? (
                <img
                    src={image}
                    alt={name}
                    loading="lazy"
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110 will-change-transform" // Optimized rendering
                />
            ) : (
                <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-400">
                    Fotoğraf Yok
                </div>
            )}

            {/* Gradient only at the bottom 2/3 to keep faces clear */}
            <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/90 via-black/50 to-transparent opacity-80 transition-opacity duration-300 pointer-events-none" />

            <div className="absolute bottom-0 inset-x-0 p-6 text-center">
                <h3 className="text-lg font-bold text-white mb-1 group-hover:text-cyan-400 transition-colors drop-shadow-md">{name}</h3>
                <p className={`text-xs font-bold uppercase tracking-widest ${color === 'cyan' ? 'text-cyan-300' : 'text-yellow-300'} drop-shadow-md`}>{role}</p>
            </div>
        </div>
    </motion.div>
);

export default TeamPage;
